// Plunger yanks (§3.3). Both ends of a connected plunger are YANKED with `partner` = the other
// end; `escape` tells the roles apart (replicated): YANK_VICTIM (0), YANK_USER (1), YANK_SAVE (2,
// a user who fired while FALLING and rises linearly to `saveEndZ`). Each end moves toward the
// other at `pullSpeed · 2 · otherMass / (m1 + m2)` until they are `stopDist` apart or `pullTicks`
// ticks pass; then each keeps 40% of its yank velocity, the victim tumbling if its yank was faster
// than the tumble threshold. An end over void then falls.
import { DT, NO_SLOT, TUNE, VOID } from '../constants';
import { BS } from '../ids';
import { MAPS } from '../maps/index';
import type { Bean, World } from '../types';
import { forceFall } from './edges';
import { isPuppet } from './world';

export const YANK_VICTIM = 0;
export const YANK_USER = 1;
export const YANK_SAVE = 2;

function pairedWith(w: World, b: Bean): Bean | null {
  if (b.partner === NO_SLOT) return null;
  const o = w.beans[b.partner];
  return o.active && o.state === BS.YANKED && o.partner === b.slot ? o : null;
}

/** Plunger connect: `user` and `victim` both enter YANKED toward each other. */
export function startYank(w: World, user: Bean, victim: Bean, save: boolean): void {
  user.state = BS.YANKED;
  user.st = 0;
  user.partner = victim.slot;
  user.escape = save ? YANK_SAVE : YANK_USER;
  victim.state = BS.YANKED;
  victim.st = 0;
  victim.partner = user.slot;
  victim.escape = YANK_VICTIM;
  victim.vz = 0;
  user.vz = save ? (TUNE.plunger.saveEndZ - user.z) / (TUNE.plunger.pullTicks * DT) : 0;
  aimYank(w, user);
  aimYank(w, victim);
}

/** A hit on a yanked bean breaks the rope (its partner finishes its yank alone). */
export function breakYank(b: Bean): void {
  b.partner = NO_SLOT;
  b.escape = 0;
}

function aimYank(w: World, b: Bean): void {
  const o = pairedWith(w, b);
  if (!o) return;
  const dx = o.pos.x - b.pos.x;
  const dy = o.pos.y - b.pos.y;
  const d = Math.hypot(dx, dy);
  const speed = (TUNE.plunger.pullSpeed * 2 * o.mass) / (b.mass + o.mass);
  b.vel.x = d > 0 ? (dx / d) * speed : 0;
  b.vel.y = d > 0 ? (dy / d) * speed : 0;
}

/** Movement pre-pass: every yanked end re-aims at its partner's current position (both move symmetrically). */
export function aimYanks(w: World): void {
  for (const b of w.beans) if (b.active && b.state === BS.YANKED && !isPuppet(w, b.slot)) aimYank(w, b);
}

/** Movement phase for a YANKED bean: along its yank velocity, no gravity; a plunger save rises toward `saveEndZ`. */
export function moveYanked(b: Bean): void {
  b.pos.x += b.vel.x * DT;
  b.pos.y += b.vel.y * DT;
  if (b.escape === YANK_SAVE) b.z = Math.min(TUNE.plunger.saveEndZ, b.z + b.vz * DT);
}

function finishYank(w: World, b: Bean): void {
  const p = TUNE.plunger;
  const speed = Math.hypot(b.vel.x, b.vel.y);
  const victim = b.escape === YANK_VICTIM;
  b.vel.x *= p.endVelMult;
  b.vel.y *= p.endVelMult;
  b.vz = 0;
  b.state = victim && speed > TUNE.knock.tumbleThreshold ? BS.TUMBLE : BS.NORMAL;
  b.st = 0;
  b.partner = NO_SLOT;
  b.escape = 0;
  const g = MAPS[w.cfg.mapId].groundAt(w, b.pos.x, b.pos.y);
  if (g === VOID) {
    forceFall(w, b.slot, b.vel.x, b.vel.y, Math.hypot(b.vel.x, b.vel.y));
    return;
  }
  b.groundZ = g;
  if (b.z < g) b.z = g;
}

/** After movement: pairs that closed to `stopDist` or ran `pullTicks` finish together; a broken rope finishes alone. */
export function endYanks(w: World): void {
  const p = TUNE.plunger;
  for (const b of w.beans) {
    if (!b.active || b.state !== BS.YANKED || isPuppet(w, b.slot)) continue;
    const o = pairedWith(w, b);
    if (o && b.st < p.pullTicks && Math.hypot(o.pos.x - b.pos.x, o.pos.y - b.pos.y) > p.stopDist) continue;
    finishYank(w, b);
    if (o && !isPuppet(w, o.slot)) finishYank(w, o);
  }
}
